import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Play, Pencil, Trash2, CalendarClock, Eye, GitCompare, HardDriveDownload } from "lucide-react";

export default function Plans() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState({});
  const [preview, setPreview] = useState(null);
  const [reconcileData, setReconcileData] = useState(null);
  const [busy, setBusy] = useState({});
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/plans"), api.get("/sources")]);
      setItems(p.data);
      setSources(Object.fromEntries(s.data.map((x) => [x.id, x.name])));
    } catch { toast.error(t("plans.loadFailed")); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const runNow = async (id) => {
    try { const r = await api.post(`/plans/${id}/run`); toast.success(t("plans.queued")); navigate(`/runs/${r.data.run_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || t("plans.failed")); }
  };
  const toggle = async (id) => {
    try { await api.post(`/plans/${id}/toggle`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("plans.failed")); }
  };
  const del = async (id) => {
    if (!window.confirm(t("plans.confirmDelete"))) return;
    try { await api.delete(`/plans/${id}`); toast.success(t("plans.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("plans.failed")); }
  };
  const showRetention = async (id) => {
    try { const r = await api.post(`/plans/${id}/retention-preview`); setPreview(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || t("plans.failed")); }
  };
  const reconcile = async (id) => {
    setBusy((b) => ({ ...b, [id]: "reconcile" }));
    try { const r = await api.post(`/plans/${id}/reconcile`); setReconcileData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || t("plans.failed")); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };
  const archiveBinlogs = async (id) => {
    setBusy((b) => ({ ...b, [id]: "binlog" }));
    try {
      const r = await api.post(`/plans/${id}/archive-binlogs`);
      toast.success(t("plans.archived", { n: r.data.binlogs?.length || 0 }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("plans.binlogUnavailable")); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };

  return (
    <div data-testid="plans-page">
      <PageHeader title={t("plans.title")} subtitle={t("plans.subtitle")}
        actions={<Button data-testid="add-plan-button" onClick={() => navigate("/plans/new")} className="rounded-none uppercase font-semibold bg-[#0A0A0A] hover:bg-[#0052FF]"><Plus className="h-4 w-4 mr-1.5" /> {t("plans.newPlan")}</Button>} />
      <div className="p-6 md:p-8">
        {items.length === 0 ? (
          <div className="grid-panel p-16 text-center">
            <p className="text-muted-foreground">{t("plans.noPlans")}</p>
            <Button onClick={() => navigate("/plans/new")} className="mt-4 rounded-none uppercase" data-testid="empty-add-plan">{t("plans.createPlan")}</Button>
          </div>
        ) : (
          <div className="grid-panel overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-5 py-3 font-medium">{t("plans.thPlan")}</th>
                  <th className="px-5 py-3 font-medium">{t("plans.thSource")}</th>
                  <th className="px-5 py-3 font-medium">{t("plans.thSchedule")}</th>
                  <th className="px-5 py-3 font-medium">{t("plans.thNextRun")}</th>
                  <th className="px-5 py-3 font-medium">{t("plans.thEnabled")}</th>
                  <th className="px-5 py-3 font-medium text-right">{t("plans.thActions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="data-grid-row" data-testid={`plan-row-${p.id}`}>
                    <td className="px-5 py-3 font-medium">{p.name}</td>
                    <td className="px-5 py-3 font-mono text-xs">{sources[p.source_id] || p.source_id}</td>
                    <td className="px-5 py-3 font-mono text-xs">{p.schedule_cron || <span className="text-muted-foreground">{t("plans.manual")}</span>}</td>
                    <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{p.next_runs?.[0] ? fmtDate(p.next_runs[0]) : "—"}</td>
                    <td className="px-5 py-3"><Switch checked={p.enabled} onCheckedChange={() => toggle(p.id)} data-testid={`toggle-plan-${p.id}`} /></td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" className="rounded-none h-8 bg-[#0052FF] hover:bg-[#0A0A0A] uppercase text-xs" onClick={() => runNow(p.id)} data-testid={`run-plan-${p.id}`}><Play className="h-3.5 w-3.5 mr-1" />{t("plans.run")}</Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8" onClick={() => showRetention(p.id)} data-testid={`retention-plan-${p.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8" disabled={busy[p.id]} onClick={() => reconcile(p.id)} data-testid={`reconcile-plan-${p.id}`} title={t("plans.titleReconcile")}><GitCompare className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8" disabled={busy[p.id]} onClick={() => archiveBinlogs(p.id)} data-testid={`binlog-plan-${p.id}`} title={t("plans.titleBinlog")}><HardDriveDownload className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8" onClick={() => navigate(`/plans/${p.id}/edit`)} data-testid={`edit-plan-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8 text-destructive" onClick={() => del(p.id)} data-testid={`delete-plan-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="retention-preview-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarClock className="h-4 w-4" />{t("plans.retentionPreview")}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {preview && Object.entries(preview).map(([destId, r]) => (
              <div key={destId} className="border border-border p-3">
                <p className="font-mono text-xs text-muted-foreground mb-2">{t("plans.destination", { id: destId })}</p>
                <p className="text-sm">{t("plans.keepDelete", { keep: r.keep?.length, delete: r.delete?.length })}</p>
                {r.delete?.length > 0 && (
                  <ul className="mt-2 font-mono text-xs text-destructive space-y-0.5">
                    {r.delete.map((x) => <li key={x.run_id}>✗ {x.run_id} · {fmtDate(x.finished_at)}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reconcileData} onOpenChange={() => setReconcileData(null)}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="reconcile-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><GitCompare className="h-4 w-4" />{t("plans.reconcileTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {reconcileData && Object.entries(reconcileData).map(([destId, r]) => (
              <div key={destId} className="border border-border p-3">
                <p className="font-medium text-sm mb-1">{r.name || destId}</p>
                <p className="text-sm">{t("plans.knownPresent", { known: r.known, present: r.present })}</p>
                <p className="text-sm">{t("plans.orphans")} <span className={r.orphans?.length ? "text-yellow-700 font-semibold" : ""}>{r.orphans?.length || 0}</span></p>
                {r.orphans?.length > 0 && (
                  <ul className="mt-2 font-mono text-[11px] text-yellow-700 space-y-0.5 max-h-32 overflow-y-auto">
                    {r.orphans.map((o, i) => <li key={i}>• {o}</li>)}
                  </ul>
                )}
                <p className="mt-2 font-mono text-[10px] text-muted-foreground">{r.note}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
